import * as React from 'react';

/** How long after one submit the same submit again is taken as the same click. */
export const SINGLE_SUBMIT_WINDOW_MS = 1000;

/**
 * A synchronous guard against the second submit of a quick double-click. A
 * form's `pending` flag comes from a react-query mutation and only reaches the
 * component after the query notifier's next tick, so two clicks ~15–40 ms apart
 * both saw `pending === false` and sent the request twice — the full-deal test
 * (2026-10-08) got three copies of one buyer question. Callers keep checking
 * `pending` for the long in-flight case; this closes the gap before it renders.
 *
 * Returns `begin(key)`: false when the SAME submission (same `key`, e.g. the
 * question text) started less than {@link SINGLE_SUBMIT_WINDOW_MS} ago, true
 * otherwise. A different submission right after (the next question) goes
 * through. Time-based on purpose: it cannot get stuck, whatever the request does.
 */
export function useSingleSubmit(): (key: string) => boolean {
  const last = React.useRef<{ key: string; at: number } | null>(null);
  return React.useCallback((key: string) => {
    const now = Date.now();
    const prev = last.current;
    if (prev && prev.key === key && now - prev.at < SINGLE_SUBMIT_WINDOW_MS) return false;
    last.current = { key, at: now };
    return true;
  }, []);
}
